import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Statistic,
  Typography,
  Row,
  Col,
  Button,
  DatePicker,
  Spin,
  Divider,
  message,
} from "antd";
import {
  DownloadOutlined,
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  BankOutlined,
  RightOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import moment from "moment";

const { Text, Title } = Typography;

const zambianColors = {
  green: "#198754",
  orange: "#de7921",
  red: "#da291c",
  black: "#000000",
};

const DIRECTUS_URL =
  process.env.REACT_APP_DIRECTUS_URL || "https://zedlenders.pickmesms.com";
const COMPANY_ID = 22;

const fmtK = (val) => {
  const num = parseFloat(val || 0);
  if (num < 0) {
    return `(K ${Math.abs(num).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })})`;
  }
  return `K ${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/** A single line item row in the income statement */
const LineItem = ({ label, amount, bold, clickable, onClick, indent, isSubtraction }) => (
  <div
    onClick={clickable ? onClick : undefined}
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 16px",
      paddingLeft: indent ? 40 : 16,
      cursor: clickable ? "pointer" : "default",
      borderBottom: "1px solid #f0f0f0",
      backgroundColor: bold ? "#fafafa" : "transparent",
      transition: clickable ? "background-color 0.2s" : "none",
    }}
    onMouseEnter={(e) => {
      if (clickable) e.currentTarget.style.backgroundColor = "#e6f7ff";
    }}
    onMouseLeave={(e) => {
      if (clickable) e.currentTarget.style.backgroundColor = bold ? "#fafafa" : "transparent";
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Text
        strong={bold}
        style={{
          fontSize: bold ? 15 : 14,
          color: bold ? zambianColors.black : "#555",
        }}
      >
        {label}
      </Text>
      {clickable && (
        <RightOutlined style={{ fontSize: 10, color: "#1890ff" }} />
      )}
    </div>
    <Text
      strong={bold}
      style={{
        fontSize: bold ? 16 : 14,
        color: isSubtraction
          ? zambianColors.red
          : amount < 0
          ? zambianColors.red
          : bold
          ? zambianColors.green
          : zambianColors.black,
        fontFamily: "monospace",
      }}
    >
      {isSubtraction && amount > 0 ? `(${fmtK(amount)})` : fmtK(amount)}
    </Text>
  </div>
);

/** Section header */
const SectionHeader = ({ title }) => (
  <div
    style={{
      padding: "16px 16px 8px",
      backgroundColor: "#fff",
    }}
  >
    <Text
      strong
      style={{
        fontSize: 13,
        color: "#888",
        textTransform: "uppercase",
        letterSpacing: 1,
      }}
    >
      {title}
    </Text>
  </div>
);

const JutemIncomeStatement = () => {
  const { company } = useContext(AppContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [interestIncome, setInterestIncome] = useState(0);
  const [interestExpense, setInterestExpense] = useState(0);
  const [commission, setCommission] = useState(0);
  const [trackingFee, setTrackingFee] = useState(0);
  const [otherExpenses, setOtherExpenses] = useState(0);

  useEffect(() => {
    if (company?.id !== COMPANY_ID) {
      message.warning("This page is only for Jutem Fund (Company ID 22)");
      navigate("/");
      return;
    }
    fetchData(selectedMonth);
  }, [company]);

  const fetchData = useCallback(async (monthMoment) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const startDate = monthMoment.clone().startOf("month").format("YYYY-MM-DD");
      const endDate = monthMoment.clone().endOf("month").format("YYYY-MM-DD");

      const [amortRes, intExpRes, opCostRes, trackersRes] = await Promise.all([
        // Interest Income: sum of profit from amortizations in this month
        axios.get(`${DIRECTUS_URL}/items/amortization`, {
          headers,
          params: {
            "filter[loan_reference][company][_eq]": COMPANY_ID,
            "filter[due_date][_gte]": startDate,
            "filter[due_date][_lte]": endDate,
            fields: "profit",
            limit: -1,
          },
        }),
        // Interest Expense
        axios.get(`${DIRECTUS_URL}/items/transactions`, {
          headers,
          params: {
            "filter[company][_eq]": COMPANY_ID,
            "filter[transaction_type][_eq]": "interest_expense",
            "filter[transaction_date][_gte]": startDate,
            "filter[transaction_date][_lte]": endDate,
            fields: "amount",
            limit: -1,
          },
        }),
        // Operational Costs
        axios.get(`${DIRECTUS_URL}/items/transactions`, {
          headers,
          params: {
            "filter[company][_eq]": COMPANY_ID,
            "filter[transaction_type][_eq]": "Operational Cost",
            "filter[transaction_date][_gte]": startDate,
            "filter[transaction_date][_lte]": endDate,
            fields: "amount,notes",
            limit: -1,
          },
        }),
        // GPS Tracking Fees: trackers issued in this month
        axios.get(`${DIRECTUS_URL}/items/gps_trackers`, {
          headers,
          params: {
            "filter[company][_eq]": COMPANY_ID,
            "filter[date_issued][_gte]": startDate,
            "filter[date_issued][_lte]": endDate,
            fields: "gps_fee",
            limit: -1,
          },
        }),
      ]);

      // Interest Income
      const amortData = amortRes.data.data || [];
      const totalInterestIncome = amortData.reduce(
        (s, r) => s + parseFloat(r.profit || 0),
        0
      );
      setInterestIncome(totalInterestIncome);

      // Interest Expense
      const intExpData = intExpRes.data.data || [];
      const totalInterestExpense = intExpData.reduce(
        (s, r) => s + parseFloat(r.amount || 0),
        0
      );
      setInterestExpense(totalInterestExpense);

      // Operational Costs — split Commission vs Other
      const opCostData = opCostRes.data.data || [];
      let totalCommission = 0;
      let totalOtherExpenses = 0;
      for (const r of opCostData) {
        const amt = parseFloat(r.amount || 0);
        const notes = (r.notes || "").toLowerCase();
        if (notes.includes("commission")) {
          totalCommission += amt;
        }
        totalOtherExpenses += amt;
      }
      setCommission(totalCommission);
      setOtherExpenses(totalOtherExpenses);

      // Tracking Fees
      const trackersData = trackersRes.data.data || [];
      const totalTrackingFee = trackersData.reduce(
        (s, r) => s + parseFloat(r.gps_fee || 0),
        0
      );
      setTrackingFee(totalTrackingFee);
    } catch (error) {
      console.error("Error fetching income statement data:", error);
      message.error("Failed to load income statement data");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleMonthChange = (date) => {
    if (date) {
      setSelectedMonth(date);
      fetchData(date);
    }
  };

  // Calculated values
  const netInterestIncome = interestIncome - interestExpense;
  const netInterestAndTracking = netInterestIncome + trackingFee;
  const netOperatingIncome = netInterestAndTracking - commission;
  const profitBeforeTax = netOperatingIncome - otherExpenses;

  const handleExport = () => {
    const monthLabel = selectedMonth.format("MMMM YYYY");
    const rows = [
      ["", ""],
      ["JUTEM FUND INCOME STATEMENT", ""],
      ["Period", monthLabel],
      ["", ""],
      ["NET INTEREST INCOME", ""],
      ["Interest Income", interestIncome.toFixed(2)],
      ["Interest Expense", `(${interestExpense.toFixed(2)})`],
      ["Net Interest Income", netInterestIncome.toFixed(2)],
      ["", ""],
      ["NET INTEREST INCOME AND TRACKING FEE", ""],
      ["Net Interest Income", netInterestIncome.toFixed(2)],
      ["Tracking Fee", trackingFee.toFixed(2)],
      ["Net Interest Income and Tracking Fee", netInterestAndTracking.toFixed(2)],
      ["", ""],
      ["NET OPERATING INCOME", ""],
      ["Net Interest Income and Tracking Fee", netInterestAndTracking.toFixed(2)],
      ["Commission", `(${commission.toFixed(2)})`],
      ["Net Operating Income", netOperatingIncome.toFixed(2)],
      ["", ""],
      ["PROFIT BEFORE TAX", ""],
      ["Net Operating Income", netOperatingIncome.toFixed(2)],
      ["Other Expenses", `(${otherExpenses.toFixed(2)})`],
      ["Profit Before Tax", profitBeforeTax.toFixed(2)],
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 40 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Income Statement");
    XLSX.writeFile(wb, `Jutem_Income_Statement_${selectedMonth.format("YYYY-MM")}.xlsx`);
    message.success("Exported successfully");
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 100 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading Income Statement...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <BankOutlined style={{ marginRight: 12, color: zambianColors.green }} />
          Income Statement
        </Title>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={handleMonthChange}
            format="MMMM YYYY"
            allowClear={false}
            style={{ width: 200 }}
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            style={{
              background: zambianColors.green,
              borderColor: zambianColors.green,
            }}
          >
            Export
          </Button>
        </div>
      </div>

      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        For the period of {selectedMonth.format("MMMM YYYY")}
      </Text>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            hoverable
            bordered={false}
            onClick={() => navigate("/amortizations")}
            style={{
              borderRadius: 8,
              background: zambianColors.green,
              boxShadow: "0 2px 8px rgba(25, 135, 84, 0.2)",
              minHeight: 130,
              cursor: "pointer",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 13, fontWeight: 500 }}>
                  Interest Income
                </span>
              }
              value={interestIncome}
              precision={2}
              prefix={<DollarOutlined style={{ color: "#fff", fontSize: 20 }} />}
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 12 }}>ZMW</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            hoverable
            bordered={false}
            onClick={() => navigate("/interest-expenses")}
            style={{
              borderRadius: 8,
              background: zambianColors.red,
              boxShadow: "0 2px 8px rgba(218, 41, 28, 0.2)",
              minHeight: 130,
              cursor: "pointer",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 13, fontWeight: 500 }}>
                  Interest Expense
                </span>
              }
              value={interestExpense}
              precision={2}
              prefix={<FallOutlined style={{ color: "#fff", fontSize: 20 }} />}
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 12 }}>ZMW</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            hoverable
            bordered={false}
            onClick={() => navigate("/expenses")}
            style={{
              borderRadius: 8,
              background: zambianColors.orange,
              boxShadow: "0 2px 8px rgba(222, 121, 33, 0.2)",
              minHeight: 130,
              cursor: "pointer",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 13, fontWeight: 500 }}>
                  Other Expenses
                </span>
              }
              value={otherExpenses}
              precision={2}
              prefix={<RiseOutlined style={{ color: "#fff", fontSize: 20 }} />}
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 12 }}>ZMW</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: profitBeforeTax >= 0 ? "#52c41a" : zambianColors.red,
              boxShadow: `0 2px 8px ${
                profitBeforeTax >= 0
                  ? "rgba(82, 196, 26, 0.2)"
                  : "rgba(218, 41, 28, 0.2)"
              }`,
              minHeight: 130,
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 13, fontWeight: 500 }}>
                  Profit Before Tax
                </span>
              }
              value={Math.abs(profitBeforeTax)}
              precision={2}
              prefix={
                <DollarOutlined style={{ color: "#fff", fontSize: 20 }} />
              }
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 12 }}>ZMW</span>}
            />
          </Card>
        </Col>
      </Row>

      {/* Income Statement */}
      <Card
        style={{
          borderRadius: 8,
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        }}
      >
        {/* Section 1: Net Interest Income */}
        <SectionHeader title="Net Interest Income" />
        <LineItem
          label="Interest Income"
          amount={interestIncome}
          clickable
          onClick={() => navigate("/amortizations")}
        />
        <LineItem
          label="Interest Expense"
          amount={interestExpense}
          clickable
          onClick={() => navigate("/interest-expenses")}
          isSubtraction
        />
        <LineItem
          label="Net Interest Income"
          amount={netInterestIncome}
          bold
        />

        <Divider style={{ margin: "8px 0" }} />

        {/* Section 2: Net Interest Income and Tracking Fee */}
        <SectionHeader title="Net Interest Income and Tracking Fee" />
        <LineItem
          label="Net Interest Income"
          amount={netInterestIncome}
          indent
        />
        <LineItem
          label="Tracking Fee"
          amount={trackingFee}
          indent
          clickable
          onClick={() => navigate("/trackers")}
        />
        <LineItem
          label="Net Interest Income and Tracking Fee"
          amount={netInterestAndTracking}
          bold
        />

        <Divider style={{ margin: "8px 0" }} />

        {/* Section 3: Net Operating Income */}
        <SectionHeader title="Net Operating Income" />
        <LineItem
          label="Net Interest Income and Tracking Fee"
          amount={netInterestAndTracking}
          indent
        />
        <LineItem
          label="Commission"
          amount={commission}
          indent
          isSubtraction
        />
        <LineItem
          label="Net Operating Income"
          amount={netOperatingIncome}
          bold
        />

        <Divider style={{ margin: "8px 0" }} />

        {/* Section 4: Profit Before Tax */}
        <SectionHeader title="Profit Before Tax" />
        <LineItem
          label="Net Operating Income"
          amount={netOperatingIncome}
          indent
        />
        <LineItem
          label="Other Expenses"
          amount={otherExpenses}
          clickable
          onClick={() => navigate("/expenses")}
          isSubtraction
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px",
            backgroundColor: profitBeforeTax >= 0 ? "#f6ffed" : "#fff2f0",
            borderTop: `2px solid ${
              profitBeforeTax >= 0 ? zambianColors.green : zambianColors.red
            }`,
            borderRadius: "0 0 8px 8px",
          }}
        >
          <Text
            strong
            style={{
              fontSize: 16,
              color: profitBeforeTax >= 0 ? zambianColors.green : zambianColors.red,
            }}
          >
            PROFIT BEFORE TAX
          </Text>
          <Text
            strong
            style={{
              fontSize: 18,
              fontFamily: "monospace",
              color: profitBeforeTax >= 0 ? zambianColors.green : zambianColors.red,
            }}
          >
            {fmtK(profitBeforeTax)}
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default JutemIncomeStatement;
